import { createFileRoute } from "@tanstack/react-router";
import { formatSek } from "@/lib/price-format";
import { Link } from "@/components/LocalizedLink";
import { useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import {
  Smartphone, Sparkles, Target, Timer, RefreshCcw, ShieldCheck,
  ArrowRight, CheckCircle2, BookOpen, Star, Users, Award, Clock, TrendingUp,
  Building2, Receipt, BarChart3, Mail, MapPin,
} from "lucide-react";
import { Disclaimer } from "@/components/Disclaimer";
import { MiniTasteTest } from "@/components/MiniTasteTest";

// Klientkomponenter som ändå inte renderar något vid SSR (LiveUrgencyBar
// returnerar null före mount, ExitIntentModal före trigger). De laddas därför
// efter hydrering istället för att ligga i startbundlen – ren TBT-vinst utan
// beteendeförändring: samma element, samma trigger, samma CTA.
const LiveUrgencyBar = lazy(() =>
  import("@/components/LiveActivity").then((m) => ({ default: m.LiveUrgencyBar }))
);
const ExitIntentModal = lazy(() =>
  import("@/components/ExitIntentModal").then((m) => ({ default: m.ExitIntentModal }))
);


import { TrustBadges } from "@/components/TrustBadges";
import { BinderPromo } from "@/components/BinderPromo";

import { CATEGORIES } from "@/lib/questions";
import { categoryLabel } from "@/lib/category-label";
import { useAuth } from "@/hooks/useAuth";
import { LoggedInHome } from "@/components/LoggedInHome";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";
import i18n from "@/i18n";
import { useHydrated } from "@/hooks/useHydrated";


export const routeOptions = {
  component: Landing,
  head: () => ({
    meta: [
      { title: "Kunskapsprov serveringstillstånd 2026 – öva gratis" },
      { name: "description", content: "Öva inför kunskapsprovet för serveringstillstånd 2026: riktiga provfrågor, provsimulering på tid och statistik per område. 5 gratis frågor per dag." },
      { property: "og:title", content: "Kunskapsprov serveringstillstånd 2026 – öva gratis" },
      { property: "og:description", content: "Riktiga provfrågor, provsimulering på tid och statistik per område. 5 gratis frågor per dag – premium från 199 kr." },

      { property: "og:url", content: "https://alkoholprovet.se/" },
      { property: "og:image", content: "https://alkoholprovet.se/og/default.jpg" },
      { name: "twitter:image", content: "https://alkoholprovet.se/og/default.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (i18n.t("landing.faqs", { returnObjects: true, lng: "sv" }) as Array<{ q: string; a: string }>).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
};

const BENEFIT_ICONS = [ShieldCheck, Award, Timer, Sparkles, Smartphone, Target];

function Landing() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const hydrated = useHydrated();

  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  // Uppskjutna klientkomponenter monteras efter hydrering (idle).
  const [deferredReady, setDeferredReady] = useState(false);
  // När snabbtestet är klart döljs heroknappar och prispuff på mobil så
  // resultatets köpknapp är det enda erbjudandet i bild.
  const [quizDone, setQuizDone] = useState(false);

  useEffect(() => {
    const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(() => setDeferredReady(true), { timeout: 2000 });
    } else {
      const id = setTimeout(() => setDeferredReady(true), 1200);
      return () => clearTimeout(id);
    }
  }, []);


  useEffect(() => {
    const el = stickySentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        // Visa sticky CTA först när användaren scrollat förbi gratisfrågorna
        setShowStickyCta(entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [user, authLoading]);

  const handleQuizDone = useCallback(() => {
    setQuizDone(true);
    // Vänta tills layouten ändrats efter quizet, scrolla sedan direkt till
    // resultatet så att poäng och köpknapp syns först.
    const go = () => {
      const el = document.getElementById("mini-test");
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: Math.max(0, top), behavior: "instant" as ScrollBehavior });
    };
    requestAnimationFrame(() => requestAnimationFrame(go));
    setTimeout(go, 250);
  }, []);

  if (!authLoading && user) {
    return <LoggedInHome user={user} />;
  }
  const benefits = (t("landing.benefits", { returnObjects: true }) as Array<{ title: string; text: string }>).map((b, i) => ({
    ...b,
    icon: BENEFIT_ICONS[i] ?? ShieldCheck,
  }));
  const testimonials = t("landing.testimonials", { returnObjects: true }) as Array<{ initials: string; role: string; text: string }>;
  const faqs = t("landing.faqs", { returnObjects: true }) as Array<{ q: string; a: string }>;
  // SSR renderar alltid svenska; guidelänkarna visas därför tills hydreringen
  // vet att besökaren valt ett annat språk (guiderna finns bara på svenska).
  const showSvGuideLinks = !hydrated || i18n.language?.startsWith("sv");


  return (
    <div className="pb-24 sm:pb-0">
      {/* HERO */}
      <section className="surface-gradient">
        <div className="container-page pt-5 sm:pt-20 pb-10 sm:pb-14">
          <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-muted-foreground" aria-label={t("trust.aria")}>
            <span>{t("landing.status.updated")}</span>
            <span aria-hidden="true">·</span>
            <span>{t("landing.status.basedOn")}</span>
            <span aria-hidden="true">·</span>
            <span>{t("cPricing.freeQuestions")}</span>
            <span aria-hidden="true" data-android-hide>·</span>
            <span data-android-hide>{t("landing.status.noSubscription")}</span>
            <span aria-hidden="true" data-android-hide>·</span>
            <span data-android-hide>{t("landing.status.payment")}</span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <h1 className="mt-2.5 sm:mt-4 font-display text-[26px] leading-[1.1] sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance">
                {t("hero.title")}
              </h1>
              <p className="mt-3 sm:mt-5 text-base sm:text-lg text-muted-foreground max-w-xl text-balance">
                {t("hero.sub")}
              </p>

              <p className="mt-3 text-[12px] sm:text-sm font-medium text-foreground/90" data-android-hide>
                {t("hero.trustRow")}
              </p>

              <div className={`mt-4 sm:mt-6 ${quizDone ? "hidden sm:flex" : "flex"} flex-col sm:flex-row gap-2 sm:gap-3`}>
                <a
                  href="#mini-test"
                  onClick={(e) => { e.preventDefault(); document.getElementById("mini-test")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className="sm:hidden inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 text-base font-semibold text-primary-foreground shadow-elevated"
                >
                  {t("hero.scrollToTest")}
                </a>
                <Link
                  to={FREE_CTA.to}
                  className="hidden sm:inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 sm:py-3.5 text-base font-semibold text-primary-foreground hover:bg-primary/90 shadow-elevated transition-all"
                >
                  {t(FREE_CTA.labelKey)} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={PREMIUM_CTA.to}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-border bg-background px-6 py-4 sm:py-3.5 text-base font-medium hover:bg-secondary"
                >
                  {t(PREMIUM_CTA.labelKey)}
                </Link>
              </div>
              <p className={`mt-2 text-[11px] sm:text-xs text-muted-foreground italic ${quizDone ? "hidden sm:block" : ""}`}>
                {t("hero.calm")}
              </p>
              <div className={`mt-2.5 sm:mt-4 ${quizDone ? "hidden sm:flex" : "flex"} flex-wrap gap-x-4 sm:gap-x-6 gap-y-1 text-[11px] sm:text-sm text-muted-foreground`}>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success" /> {t("trust.freeNoCard")}</span>
                <span className="hidden sm:inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" /> {t("trust.noSubscription")}</span>
                <span className="inline-flex items-center gap-1.5" data-android-hide><ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success" /> {t("trust.securePayment")}</span>
              </div>

            </div>

            <div className="lg:col-span-5">
              {/* Mobil-prisankare: lyfter Premium-priset över fold på mobil */}
              <div className={`sm:hidden mb-3 ${quizDone ? "hidden" : "flex"} items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3.5 py-2.5`} data-android-hide>
                <span className="text-[12px] font-medium text-foreground">
                  {t("hero.mobilePriceAnchor")}
                </span>
                <Link to="/pricing" className="text-[12px] font-semibold text-primary hover:underline whitespace-nowrap">
                  Se planer →
                </Link>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-accent/10 blur-2xl" />
                <div className="relative scroll-mt-20" id="mini-test">
                  <MiniTasteTest onDone={handleQuizDone} />
                  <div ref={stickySentinelRef} aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* SOCIAL PROOF STRIP */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <Stat icon={Star} value={t("stats.rating")} label={t("stats.ratingLabel")} />
          <Stat icon={Users} value={t("stats.users")} label={t("stats.usersLabel")} />
          <Stat icon={Award} value={t("stats.passRate")} label={t("stats.passLabel")} />
          <Stat icon={Clock} value={t("stats.avgSession")} label={t("stats.avgLabel")} />
        </div>
        <div className="container-page pb-6">
          {deferredReady && (
            <Suspense fallback={null}>
              <LiveUrgencyBar />
            </Suspense>
          )}
        </div>
        <div className="container-page pb-8" data-android-hide>
          <TrustBadges />
        </div>
      </section>



      {/* BENEFITS */}
      <section className="container-page py-16 sm:py-24">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-balance">
            {t("landing.benefitsTitle")}
          </h2>
          <p className="mt-3 text-muted-foreground">
            {t("landing.benefitsLead")}
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                <b.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{b.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EGENKONTROLLPÄRM */}
      <BinderPromo className="pb-16 sm:pb-24" />

      {/* TESTIMONIALS */}
      <section className="container-page pb-16 sm:pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">{t("landing.testimonialsTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.testimonialsLead")}</p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {testimonials.map((tm) => (
            <figure key={tm.initials} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex gap-0.5 text-primary">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-primary" />
                ))}
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed">&ldquo;{tm.text}&rdquo;</blockquote>
              <figcaption className="mt-3 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{tm.initials}</span> · {tm.role}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>



      {/* CATEGORIES */}
      <section className="container-page pb-16 sm:pb-24">
        <div className="rounded-3xl bg-surface-deep text-primary-foreground p-8 sm:p-12">
          <div className="flex items-center gap-2 text-sm text-primary-foreground/90">
            <BookOpen className="h-4 w-4" /> {t("landing.areasCount")}
          </div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("landing.areasTitle")}</h2>
          <div className="mt-8 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <span key={c} className="rounded-full bg-white/10 backdrop-blur px-3.5 py-1.5 text-sm">{categoryLabel(c)}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="priser" className="container-page pb-16 sm:pb-24" data-android-hide>
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">{t("landing.pricingTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.pricingLead")}</p>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto">
          <PriceCard label={t("pricing.days10")} priceIncl={199} days={10} plan="premium_10d_once" />
          <PriceCard label={t("pricing.days30")} priceIncl={349} days={30} plan="premium_30d_once_v2" featured savingsLabel={t("pricing.savings")} />
          <PriceCard label={t("pricing.days90")} priceIncl={599} days={90} plan="premium_90d_once" />
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">{t("landing.pricingVat")}</p>
      </section>



      {/* FAQ */}
      <section className="container-page pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-3 gap-10">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">{t("landing.faqTitle")}</h2>
            <p className="mt-3 text-muted-foreground">{t("landing.faqLead")}</p>
            <Link to="/faq" className="mt-4 inline-flex text-accent hover:underline text-sm">{t("landing.faqAll")}</Link>
          </div>
          <div className="lg:col-span-2 space-y-3">
            {faqs.slice(0, 6).map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
                <summary className="cursor-pointer list-none flex justify-between gap-4 font-medium">
                  <span>{f.q}</span>
                  <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SVENSKA GUIDELÄNKAR – internlänkar med sökordsankare (endast sv) */}
      {showSvGuideLinks && (
        <section className="container-page pb-16 sm:pb-24">
          <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Populära guider och gratis prov</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Uppdaterat efter 2026 års alkohollag – kravet på matservering, eget kök och sittplatser är avskaffat sedan 1 juni 2026.
            </p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {[
                { to: "/gratis-ovningsfragor-serveringstillstand", label: "Alkoholtillstånd prov gratis – gratis prov på serveringstillstånd" },
                { to: "/utskankningstillstand", label: "Utskänkningstillstånd – steg för steg 2026" },
                { to: "/utbildning-serveringstillstand", label: "Utbildning serveringstillstånd – kurs eller plugga själv?" },
                { to: "/serveringstillstand-slutet-sallskap", label: "Serveringstillstånd för slutet sällskap" },
                { to: "/kunskapsbank/restaurang-lon-regler-personal", label: "Restaurang lön 18 år 2026 – lönestege och regler" },
                { to: "/serveringstillstand-och-kunskapsprov", label: "Alla guider om serveringstillstånd" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                    <ArrowRight className="h-4 w-4" /> {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}



      {/* CITY LINKS */}
      <section className="container-page pb-16 sm:pb-24">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-balance max-w-2xl">
          {t("landing.cities.title")}
        </h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          {t("landing.cities.lead")}
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <CityCard
            to="/serveringstillstand-stockholm"
            city="Stockholm"
            text={t("landing.cities.cityText", { city: "Stockholm" })}
          />
          <CityCard
            to="/serveringstillstand-goteborg"
            city="Göteborg"
            text={t("landing.cities.cityText", { city: "Göteborg" })}
          />
          <CityCard
            to="/serveringstillstand-malmo"
            city="Malmö"
            text={t("landing.cities.cityText", { city: "Malmö" })}
          />
          <CityCard
            to="/foretag"
            city={t("landing.cities.companyLabel")}
            text={t("landing.cities.companyText")}
            icon={Building2}
          />
        </div>
      </section>

      {/* B2B */}
      <section className="container-page pb-16 sm:pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-secondary/60 via-card to-accent/5 p-8 sm:p-12 shadow-card">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-accent/10 blur-3xl" aria-hidden />
          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                <Building2 className="h-3.5 w-3.5" /> {t("landing.b2b.badge")}
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-balance">
                {t("landing.b2b.title")}
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                {t("landing.b2b.lead")}
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Receipt, title: t("landing.b2b.b1Title"), text: t("landing.b2b.b1Text") },
                  { icon: BarChart3, title: t("landing.b2b.b2Title"), text: t("landing.b2b.b2Text") },
                  { icon: Users, title: t("landing.b2b.b3Title"), text: t("landing.b2b.b3Text") },
                ].map((f) => (
                  <div key={f.title} className="rounded-2xl border border-border bg-background/60 p-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <f.icon className="h-4 w-4" />
                    </div>
                    <div className="mt-2 text-sm font-semibold">{f.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{f.text}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-elevated">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold">
                  {t("landing.b2b.cta")}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("landing.b2b.ctaAlt")}
                </p>
                <Link
                  to="/kontakt"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 shadow-elevated"
                >
                  {t("landing.b2b.cta")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-page pb-16 sm:pb-24">
        <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 text-center shadow-elevated">
          <div className="inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
            <TrendingUp className="h-3.5 w-3.5" /> {t("landing.finalBadge")}
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-balance">
            {t("landing.finalTitle")}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            {t("landing.finalLead")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-medium text-primary-foreground hover:bg-primary/90 shadow-elevated">
              {t(FREE_CTA.labelKey)} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3.5 text-base font-medium hover:bg-secondary">
              {t(PREMIUM_CTA.labelKey)}
            </Link>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="container-narrow pb-16">
        <Disclaimer />
      </section>

      {/* STICKY MOBILE CTA */}
      <div
        className={`fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-border bg-background/95 backdrop-blur p-3 transition-all duration-300 ${
          showStickyCta ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        aria-hidden={!showStickyCta}
      >
        <div className="flex gap-2">
          <Link to={FREE_CTA.to} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-3.5 text-sm font-semibold text-primary-foreground shadow-elevated">
            {t(FREE_CTA.labelKey)} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to={PREMIUM_CTA.to} className="inline-flex items-center justify-center gap-1 rounded-xl border border-border bg-background px-3 py-3.5 text-sm font-medium whitespace-nowrap">
            Premium {formatSek(199)}
          </Link>
        </div>
        <p className="mt-1.5 text-center text-[11px] text-muted-foreground" data-android-hide>{t("hero.priceReassure")}</p>
      </div>


      
      {deferredReady && (
        <Suspense fallback={null}>
          <ExitIntentModal />
        </Suspense>
      )}
    </div>

  );
}

function CityCard({ to, city, text, icon: Icon }: { to: string; city: string; text: string; icon?: typeof MapPin }) {
  const { t } = useTranslation();
  const CardIcon = Icon ?? MapPin;
  return (
    <Link
      to={to}
      className="group rounded-2xl border border-border bg-card p-6 shadow-card hover:shadow-elevated hover:border-accent/40 transition-all"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
        <CardIcon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold">{city}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent group-hover:underline">
        {t("landing.cities.readMore")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

function Stat({ icon: Icon, value, label }: { icon: typeof Star; value: string; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <Icon className="h-5 w-5 text-primary" />
      <div className="mt-1.5 font-display text-xl sm:text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}


function PriceCard({ label, priceIncl, days, plan, featured, savingsLabel }: { label: string; priceIncl: number; days: number; plan: "premium_10d_once" | "premium_30d_once_v2" | "premium_90d_once"; featured?: boolean; savingsLabel?: string }) {
  const { t } = useTranslation();
  const perDay = (priceIncl / days).toFixed(2).replace(".", ",");
  const features = t("landing.priceCardFeatures", { returnObjects: true }) as string[];
  return (
    <div
      className={`relative rounded-2xl border p-4 sm:p-8 ${
        featured
          ? "border-primary bg-card shadow-elevated"
          : "border-border bg-card shadow-card"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-3 sm:left-6 rounded-full bg-primary px-2.5 py-1 text-[10px] sm:text-xs font-medium text-primary-foreground">
          {t("pricing.mostChosen")}
        </div>
      )}
      <div className="font-display text-base sm:text-xl font-semibold">{label}</div>
      <div className="mt-2 sm:mt-3 flex items-baseline gap-1">
        <span className="font-display text-3xl sm:text-5xl font-bold">{formatSek(priceIncl)}</span>
      </div>
      <p className="mt-1 text-[11px] sm:text-sm text-muted-foreground">{t("pricing.inclVat")} · {t("pricing.perDay", { amount: perDay })}</p>
      {savingsLabel && (
        <p className="mt-2 inline-block rounded-md bg-success/10 px-2 py-1 text-[10px] sm:text-xs font-medium text-success">{savingsLabel}</p>
      )}
      <ul className="mt-4 sm:mt-5 space-y-2 text-xs sm:text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/checkout"
        search={{ plan } as any}
        data-purchase-cta
        className={`mt-5 sm:mt-6 inline-flex w-full justify-center rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 text-sm font-medium transition-colors ${
          featured
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-border hover:bg-secondary"
        }`}
      >
        {t("landing.priceCardSelect", { label: label.toLowerCase() })}
      </Link>
    </div>
  );
}
